import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Sun, Moon, Shield } from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';
import { useAppStore } from '../../store/useAppStore';
// Strapline-cropped marks: the source art had "IN BUSINESS SINCE 1926" baked in at
// ~37px tall, which rendered ~3px on screen and read as blurry. Cropped off and
// re-set as live text below, so it is sharp at any DPI and editable in code.
import catesLogo from '../../assets/cates_logo_mark.png';
import catesWhiteLogo from '../../assets/cates_logo_mark_white.png';

const BRAND_BLUE = '#0072c6';
const BRAND_LIGHT = '#1493e6';
const BRAND_SILVER = '#e6edf5';
const BRAND_DARK = '#050608';

const services = [
  { label: 'Roof Replacement', slug: 'roof-replacement' },
  { label: 'Insurance Claim Assistance', slug: 'insurance-claims' },
  { label: 'Roof Repair', slug: 'roof-repair' },
  { label: 'Roof Inspection', slug: 'roof-inspection' },
  { label: 'Commercial Roofing', slug: 'commercial-roofing' },
  { label: 'Emergency Service', slug: 'emergency-roofing' },
  { label: 'Roof Cleaning', slug: 'roof-cleaning' },
  { label: 'Gutter Cleaning', slug: 'gutter-cleaning' },
];

const PublicNavbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { theme, toggleTheme, isAuthenticated, user } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isDark = theme === 'dark';
  const linkColor = isDark ? 'white' : BRAND_DARK;
  const textShadow = isDark ? '0 1px 4px rgba(0,0,0,0.6)' : 'none';
  const navBackground = isDark
    ? (scrolled ? 'rgba(5,6,8,0.97)' : 'rgba(5,6,8,0.92)')
    : (scrolled ? 'rgba(230,237,245,0.98)' : 'rgba(230,237,245,0.94)');
  const menuBackground = isDark ? BRAND_DARK : BRAND_SILVER;
  const menuText = isDark ? 'white' : BRAND_DARK;
  return (
    <>
      {/* Top accent bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001,
        height: '3px',
        background: `linear-gradient(90deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 50%, ${BRAND_LIGHT} 100%)`,
      }} />

      <nav style={{
        position: 'fixed', top: '3px', left: 0, right: 0, zIndex: 1000,
        background: navBackground,
        backdropFilter: 'blur(14px)',
        borderBottom: scrolled ? `2px solid rgba(0,114,198,0.18)` : '1px solid transparent',
        boxShadow: scrolled ? '0 2px 24px rgba(5,8,15,0.12)' : isDark ? 'none' : '0 1px 18px rgba(5,6,8,0.08)',
        transition: 'all 0.3s ease',
      }}>
        <div className="container-xl" style={{ padding: '0 1.5rem' }}>
          <div className="nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

            {/* Logo */}
            <Link to={ROUTES.HOME} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none', flexShrink: 0 }}>
              {!logoError ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className="nav-logo" style={{
                    transition: 'all 0.3s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <img
                      src={isDark ? catesWhiteLogo : catesLogo}
                      alt="Cates Roofing & Contracting"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      onError={() => setLogoError(true)}
                    />
                  </div>
                  <span className="nav-strapline" style={{
                    color: isDark ? 'rgba(255,255,255,0.82)' : 'rgba(5,6,8,0.72)',
                    fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap', lineHeight: 1,
                  }}>
                    – Family Legacy {APP_CONFIG.familyBusinessLegacySince} –
                  </span>
                </div>
              ) : (
                <div style={{
                  width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem',
                  background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_LIGHT})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Shield size={18} color="white" strokeWidth={2.5} />
                </div>
              )}
            </Link>

            {/* Desktop Nav */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }} className="hidden-mobile">
              {[{ to: ROUTES.HOME, label: 'Home' }].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    textDecoration: 'none', transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = BRAND_LIGHT; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >{item.label}</Link>
              ))}

              {/* Services Dropdown */}
              <div style={{ position: 'relative' }} onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    background: 'none', border: 'none', cursor: 'pointer',
                    transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = BRAND_LIGHT; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >
                  Services <ChevronDown size={14} />
                </button>
                {servicesOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, minWidth: '220px',
                    background: menuBackground, border: `1px solid rgba(0,114,198,0.18)`,
                    borderRadius: '1rem', padding: '0.5rem',
                    boxShadow: '0 20px 48px rgba(5,8,15,0.18)', zIndex: 100,
                  }}>
                    {services.map(s => (
                      <Link key={s.slug} to={`/services/${s.slug}`} style={{
                        display: 'block', padding: '0.625rem 0.875rem', borderRadius: '0.625rem',
                        fontSize: '0.875rem', color: menuText, textDecoration: 'none',
                        transition: 'all 0.15s', fontWeight: 500,
                      }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = BRAND_BLUE; (e.currentTarget as HTMLElement).style.color = 'white'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = menuText; }}
                      >{s.label}</Link>
                    ))}
                  </div>
                )}
              </div>

              {[
                { to: ROUTES.GALLERY, label: 'Gallery' },
                { to: ROUTES.ABOUT, label: 'About' },
                { to: ROUTES.BLOG, label: 'Blog' },
                { to: ROUTES.CONTACT, label: 'Contact' },
              ].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    textDecoration: 'none', transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = BRAND_LIGHT; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >{item.label}</Link>
              ))}
            </div>

            {/* Right Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }} className="hidden-mobile">
              <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                fontSize: '0.875rem', fontWeight: 700, color: linkColor,
                textDecoration: 'none', textShadow,
              }}>
                <Phone size={14} /> {APP_CONFIG.phone}
              </a>
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', display: 'flex', alignItems: 'center' }}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    const portal = user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'contractor' ? '/contractor/dashboard' : '/portal/dashboard';
                    navigate(portal);
                  }}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '100px', fontSize: '0.8rem',
                    fontWeight: 700, color: linkColor, textTransform: 'uppercase', letterSpacing: '0.04em',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,114,198,0.34)'}`,
                    background: 'none', cursor: 'pointer', transition: 'all 0.2s', textShadow,
                  }}
                >
                  My Portal
                </button>
              ) : (
                <Link
                  to={ROUTES.LOGIN}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '100px', fontSize: '0.8rem',
                    fontWeight: 700, color: linkColor, textTransform: 'uppercase', letterSpacing: '0.04em',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,114,198,0.34)'}`,
                    textDecoration: 'none', transition: 'all 0.2s', textShadow,
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND_LIGHT; (e.currentTarget as HTMLElement).style.color = BRAND_LIGHT; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,114,198,0.34)'; (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >
                  Sign In
                </Link>
              )}
              <Link to={ROUTES.QUOTE} className="btn-primary" style={{ padding: '0.5rem 1.25rem', fontSize: '0.8rem' }}>
                Free Estimate
              </Link>
            </div>

            {/* Mobile Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} className="mobile-only">
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', textShadow }}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button onClick={() => setMobileOpen(!mobileOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', textShadow }}>
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileOpen && (
            <div style={{
              borderTop: `1px solid rgba(20,147,230,0.18)`, padding: '0.5rem 0 1rem',
              background: isDark ? 'rgba(5,8,15,0.98)' : 'rgba(255,255,255,0.98)', borderRadius: '0 0 1rem 1rem',
              boxShadow: '0 8px 32px rgba(5,8,15,0.28)',
            }}>
              {[
                { to: ROUTES.HOME, label: 'Home' },
                { to: ROUTES.SERVICES, label: 'Services' },
                { to: ROUTES.GALLERY, label: 'Gallery' },
                { to: ROUTES.ABOUT, label: 'About' },
                { to: ROUTES.BLOG, label: 'Blog' },
                { to: ROUTES.CONTACT, label: 'Contact' },
              ].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    display: 'block', padding: '0.75rem 1.25rem',
                    fontSize: '0.875rem', fontWeight: 700, color: linkColor,
                    textDecoration: 'none', borderBottom: '1px solid rgba(0,114,198,0.07)',
                    transition: 'color 0.2s', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = BRAND_LIGHT)}
                  onMouseLeave={e => (e.currentTarget.style.color = linkColor)}
                >
                  {item.label}
                </Link>
              ))}
              <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: BRAND_LIGHT, fontWeight: 700, textDecoration: 'none' }}>
                  <Phone size={16} /> {APP_CONFIG.phone}
                </a>
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ textAlign: 'center' }}>Get Free Estimate</Link>
                <Link to={ROUTES.LOGIN} className="btn-secondary" style={{ textAlign: 'center' }}>Sign In</Link>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @media (max-width: 900px) { .hidden-mobile { display: none !important; } }
          @media (min-width: 901px) { .mobile-only { display: none !important; } }

          /* Logo scales with the bar; source art is 1844x853 so it stays sharp at every step */
          .nav-row { height: 6rem; }
          .nav-logo { width: 15rem; height: 4.25rem; }
          .nav-strapline { font-size: 0.6rem; margin-top: 0.28rem; }
          @media (max-width: 900px) {
            .nav-row { height: 5.25rem; }
            .nav-logo { width: 12.5rem; height: 3.6rem; }
            .nav-strapline { font-size: 0.54rem; letter-spacing: 0.12em; }
          }
          @media (max-width: 420px) {
            .nav-row { height: 4.75rem; }
            .nav-logo { width: 10rem; height: 2.9rem; }
            .nav-strapline { font-size: 0.48rem; letter-spacing: 0.1em; }
          }
        `}</style>
      </nav>
    </>
  );
};

export default PublicNavbar;
